import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  MapPin, Briefcase,
  ExternalLink, Loader2, Phone, Mail,
  Share2, Globe, AlertCircle, Send,
  ChevronDown, ChevronUp, X, Image as ImageIcon,
  ArrowLeft, ArrowRight, AlignJustify,
} from "lucide-react";
import externalAPI from "./ExternalAPI";
import { getUploadUrl, getImageRoot, shareWithImage, isLive } from "../Utils/Utilities";
import {
  getMasterDataFromConfig,
  getConfigData,
  saveLeadInfo,
  getCustomerInfo,
} from "../Utils/Storage";
import { setCookie } from "../Utils/CookieHandler";
import { toast } from "sonner";
import { segments, industries } from "../Components/Enums/DataEnums";
import { BsWhatsapp } from "react-icons/bs";
import { Input } from "../Components/UI/input";
import { Textarea } from "../Components/UI/textarea";
import { Button } from "../Components/UI/button";
import { Label } from "../Components/UI/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../Components/UI/dialog";
import { Drawer, DrawerContent } from "../Components/UI/drawer";
import useIsMobile from "../Hooks/useIsMobile";
import { SpeechControls } from "../Components/Common/SpeechControls";
import MobileInput from "../Components/Common/MobileInput";
import MenuDrawer from "../Components/Layout/MenuDrawer";


// Static placeholder layout for Webstore background when awaiting visitor details
const WebStoreSkeleton = () => (
  <div className="w-full max-w-7xl space-y-4 sm:space-y-6 lg:pb-8 relative pointer-events-none select-none">
    {/* Full-Width Top Header Card */}
    <div className="bg-white border border-[var(--border)] rounded-2xl p-5 sm:p-6 lg:p-7 shadow-[0_2px_10px_rgba(0,0,0,0.04)] relative overflow-hidden flex flex-col lg:flex-row items-center justify-between gap-5 sm:gap-6 lg:gap-8">
      {/* Brand Row: Logo on Left, Name & Tagline on Right */}
      <div className="flex flex-row items-start justify-start gap-3.5 sm:gap-4 lg:gap-6 w-full lg:w-auto min-w-0">
        {/* Logo Badge */}
        <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-18 lg:h-18 rounded-xl sm:rounded-2xl border border-[var(--border)] bg-zinc-100/80 flex items-center justify-center shrink-0">
          <ImageIcon className="h-7 w-7 sm:h-8 sm:w-8 text-zinc-400 opacity-60" />
        </div>

        {/* Name and Tagline stacked up and down */}
        <div className="flex flex-col justify-start items-start text-left min-w-0 flex-1">
          <h1 className="text-2xl sm:text-2xl lg:text-2xl xl:text-3xl font-bold text-[var(--text-color)] tracking-tight leading-tight">
            Business Webstore
          </h1>
          <p className="text-sm sm:text-sm lg:text-base text-[var(--sub-text-color)] font-normal leading-normal mt-1 max-w-xl text-left">
            Supplier of Quality Products & Services
          </p>
        </div>
      </div>

      {/* Action Icons Row */}
      <div className="flex items-center justify-between sm:justify-around lg:justify-end gap-2 sm:gap-4 lg:gap-4 xl:gap-5 w-full max-w-sm sm:max-w-md lg:max-w-none lg:w-auto mx-auto lg:mx-0 py-3 lg:pt-0 border-[var(--border)]/60 shrink-0 px-2 sm:px-4 lg:px-0">
        {/* 1. Inquire */}
        <div className="flex-1 lg:flex-initial flex flex-col items-center gap-1.5 max-w-[72px] sm:max-w-[80px] lg:max-w-none">
          <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-sahi-orange-light text-sahi-orange flex items-center justify-center shadow-xs">
            <Send className="h-5 w-5 text-sahi-orange" />
          </div>
          <span className="text-xs font-medium text-[var(--sub-text-color)]">
            Inquire
          </span>
        </div>

        {/* 2. Call */}
        <div className="flex-1 lg:flex-initial flex flex-col items-center gap-1.5 max-w-[72px] sm:max-w-[80px] lg:max-w-none">
          <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-sahi-blue-light text-sahi-dark-blue flex items-center justify-center shadow-xs">
            <Phone className="h-5 w-5 text-sahi-dark-blue" />
          </div>
          <span className="text-xs font-medium text-[var(--sub-text-color)]">
            Call
          </span>
        </div>

        {/* 3. WhatsApp */}
        <div className="flex-1 lg:flex-initial flex flex-col items-center gap-1.5 max-w-[72px] sm:max-w-[80px] lg:max-w-none">
          <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[var(--sahi-green-light)] text-[var(--sahi-green)] flex items-center justify-center shadow-xs">
            <BsWhatsapp className="h-5 w-5 text-[var(--sahi-green)]" />
          </div>
          <span className="text-xs font-medium text-[var(--sub-text-color)]">
            WhatsApp
          </span>
        </div>

        {/* 4. Share */}
        <div className="flex-1 lg:flex-initial flex flex-col items-center gap-1.5 max-w-[72px] sm:max-w-[80px] lg:max-w-none">
          <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gray-200 text-[var(--bg-grey)] border border-zinc-200 flex items-center justify-center">
            <Share2 className="h-5 w-5 text-zinc-700" />
          </div>
          <span className="text-xs font-medium text-[var(--sub-text-color)]">
            Share
          </span>
        </div>
      </div>
    </div>

    {/* 2-Column Main Content Grid */}
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 items-start">
      {/* Left Column: Catalog & Form */}
      <div className="lg:col-span-8 space-y-4 sm:space-y-6">
        {/* Catalog Section */}
        <div className="bg-white border border-[var(--border)] rounded-xl p-4 sm:p-6 md:p-7 shadow-xs space-y-4">
          <div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-[var(--text-color)]">
              Catalog
            </h2>
            <p className="text-xs md:text-sm text-[var(--sub-text-color)] mt-1">
              Click on an offering to inquire.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3.5 sm:gap-4 !mt-0">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex flex-col">
                <div className="relative aspect-square w-full rounded-xl overflow-hidden bg-zinc-100 flex items-center justify-center border border-[var(--border)] shadow-xs">
                  <ImageIcon className="w-6 h-6 text-zinc-300 opacity-60" />
                </div>
                <div className="hidden sm:flex pt-2 pb-1 px-1 flex-col items-center text-center w-full">
                  <div className="h-3.5 bg-zinc-200/80 rounded w-3/4 mx-auto" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Inquiry Card */}
        <div className="bg-white border border-[var(--border)] rounded-xl p-4 sm:p-6 md:p-7 shadow-xs space-y-3">
          <h3 className="text-base sm:text-lg font-bold text-[var(--text-color)]">
            Your Requirement
          </h3>
          <div className="h-24 rounded-lg border border-[var(--border)] bg-zinc-50/70 p-3 flex items-start text-xs text-zinc-400">
            Tell us about your requirement...
          </div>
        </div>
      </div>

      {/* Right Column / Sidebar */}
      <div className="lg:col-span-4 space-y-4 sm:space-y-6">
        <div className="bg-white border border-[var(--border)] rounded-xl p-4 sm:p-5 md:p-6 shadow-xs space-y-3">
          <div className="text-xs font-semibold text-[var(--sub-text-color)]">
            Contact Information
          </div>
          <div className="space-y-2.5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[var(--sahi-green-light)] text-[var(--sahi-green)] flex items-center justify-center">
                <Mail className="h-4 w-4" />
              </div>
              <span className="text-sm text-zinc-600 font-medium">contact@business.com</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[var(--sahi-green-light)] text-[var(--sahi-green)] flex items-center justify-center">
                <Phone className="h-4 w-4" />
              </div>
              <span className="text-sm text-zinc-600 font-medium">+91 98765 43210</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default function WebStore() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Support m parameter (primary), or agnID / agencyID / mobile
  const paramM = searchParams.get("m") || searchParams.get("agencyID") || searchParams.get("agnID") || searchParams.get("mobile");
  const rawSrc = (searchParams.get("src") || searchParams.get("source") || "").trim();
  const paramSrc = rawSrc ? rawSrc : "SahiClub";
  const paramI = (searchParams.get("i") || searchParams.get("I") || "").trim();
  const showBackButton = paramI === "1";

  const handleBack = () => {
    window.history.back();
  };

  const handleLogoClick = () => {
    setCookie("DefaultPage", "/");
    navigate("/");
  };

  const [catalogFromAPI, setCatalogFromAPI] = useState([]);
  const [zoomImageUrl, setZoomImageUrl] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [isMenuDrawerOpen, setIsMenuDrawerOpen] = useState(false);

  const handleCloseZoom = () => {
    setZoomImageUrl(null);
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
  };

  // When an image is fullscreen, Escape key should close only the image, preserving the modal
  useEffect(() => {
    if (!zoomImageUrl) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleCloseZoom();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [zoomImageUrl]);

  // Helper to check if visitor already has LeadInfo or Jarvis credentials
  const checkHasLeadOrJarvis = () => {
    try {
      const customer = getCustomerInfo(paramM);
      return Boolean(customer && (customer.mobile || customer.name));
    } catch {
      return false;
    }
  };

  const hasKnownUserOnMount = useRef(checkHasLeadOrJarvis());
  // If visitor is unknown on mount, open the Welcome contact modal/drawer immediately
  const [isContactPromptOpen, setIsContactPromptOpen] = useState(!hasKnownUserOnMount.current);
  const [submittingContactDetails, setSubmittingContactDetails] = useState(false);
  const [contactForm, setContactForm] = useState(() => {
    const customer = getCustomerInfo(paramM);
    return {
      company: customer?.company || "",
      name: customer?.name || "",
      mobile: customer?.mobile || "",
      cc: customer?.cc || "91",
      email: customer?.email || "",
    };
  });

  // Offering Inquiry Modal States
  const [selectedOffering, setSelectedOffering] = useState(null);
  const [submittingOfferingInquiry, setSubmittingOfferingInquiry] = useState(false);
  const [mobileInquiryStep, setMobileInquiryStep] = useState(1);
  const [offeringInquiryForm, setOfferingInquiryForm] = useState(() => {
    const customer = getCustomerInfo(paramM);
    return {
      company: customer?.company || "",
      name: customer?.name || "",
      mobile: customer?.mobile || "",
      cc: customer?.cc || "91",
      email: customer?.email || "",
      needs: "",
    };
  });

  const resetOfferingModalState = () => {
    setSelectedOffering(null);
    setMobileInquiryStep(1);
    const currentAgencyID = profileData?.AgencyID || paramM;
    const customer = getCustomerInfo(currentAgencyID);
    setOfferingInquiryForm({
      company: customer?.company || "",
      name: customer?.name || "",
      mobile: customer?.mobile || "",
      cc: customer?.cc || "91",
      email: customer?.email || "",
      needs: "",
    });
  };

  const handleDrawerFieldFocus = (e) => {
    const field = e.target.closest(".drawer-field");
    const container = field?.closest(".drawer-form-scroll");
    if (field && container) {
      const scrollFieldToTop = () => {
        const containerRect = container.getBoundingClientRect();
        const fieldRect = field.getBoundingClientRect();
        const maxScroll = container.scrollHeight - container.clientHeight;
        const targetScrollTop = container.scrollTop + (fieldRect.top - containerRect.top) - 8;
        container.scrollTo({
          top: Math.min(maxScroll, Math.max(0, targetScrollTop)),
          behavior: "smooth",
        });
      };
      setTimeout(scrollFieldToTop, 100);
      setTimeout(scrollFieldToTop, 320);
    }
  };

  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [isInquiryOpen, setIsInquiryOpen] = useState(false);
  const [showAllCatalogMobile, setShowAllCatalogMobile] = useState(false);
  const formRef = useRef(null);
  const firstNewlyVisibleRef = useRef(null);
  const shouldScrollToNewProducts = useRef(false);

  useEffect(() => {
    if (showAllCatalogMobile && shouldScrollToNewProducts.current) {
      shouldScrollToNewProducts.current = false;
      const timer = setTimeout(() => {
        if (firstNewlyVisibleRef.current) {
          firstNewlyVisibleRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 80);
      return () => clearTimeout(timer);
    }
  }, [showAllCatalogMobile]);

  // Inquiry Form State
  const [submittingInquiry, setSubmittingInquiry] = useState(false);
  const [isDirectInquiryContactDrawerOpen, setIsDirectInquiryContactDrawerOpen] = useState(false);
  const [inquiryForm, setInquiryForm] = useState(() => {
    const customer = getCustomerInfo(paramM);
    return {
      company: customer?.company || "",
      name: customer?.name || "",
      mobile: customer?.mobile || "",
      cc: customer?.cc || "91",
      email: customer?.email || "",
      product: "",
      needs: "",
    };
  });


  const resolveVisitorInfo = (overrideInfo = null) => {
    const customer = getCustomerInfo(paramM);
    let jarvisAuth = null;
    try {
      const jarvisRaw = localStorage.getItem("Jarvis");
      if (jarvisRaw) {
        jarvisAuth = JSON.parse(jarvisRaw);
      }
    } catch { }

    const authFirst = jarvisAuth && Array.isArray(jarvisAuth) && jarvisAuth.length > 0 ? jarvisAuth[0] : null;

    const name = overrideInfo?.name || customer?.name || authFirst?.UserName || authFirst?.Name || "";
    const rawMobile = String(overrideInfo?.mobile || customer?.mobile || authFirst?.Mobile || authFirst?.ContactNo || "").trim();
    const rawCC = String(overrideInfo?.cc || customer?.cc || authFirst?.CC || authFirst?.cc || "91").trim();
    const cleanCC = rawCC.replace(/\D/g, "") || "91";
    const cleanMobile = rawMobile.replace(/\D/g, "");

    let mobile = cleanMobile;
    if (cleanMobile) {
      if (!cleanMobile.startsWith(cleanCC)) {
        mobile = `${cleanCC}_${cleanMobile}`;
      }
    }

    const company = overrideInfo?.company || customer?.company || authFirst?.AgencyName || "";
    const email = overrideInfo?.email || customer?.email || authFirst?.Email || "";
    const visitorAgencyID = overrideInfo?.visitorAgencyID || authFirst?.AgencyID || 0;
    const visitorAgencyName = overrideInfo?.visitorAgencyName || authFirst?.AgencyName || company || "";
    const visitorUserID = overrideInfo?.visitorUserID || authFirst?.UserID || 0;
    const changeType = overrideInfo?.changeType || "VISIT";

    return {
      name, mobile, cc: cleanCC, company, email, visitorAgencyID, visitorAgencyName, visitorUserID, changeType
    };
  };

  const fetchCompanyProfile = async (visitorInfo = null) => {
    if (!paramM) {
      setLoading(false);
      setErrorMsg("Please provide a valid agency ID or mobile number in the URL parameter.");
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    setLogoError(false);
    try {
      const fullVisitorInfo = resolveVisitorInfo(visitorInfo);
      const response = await externalAPI.getAgencyVitals(paramM, fullVisitorInfo);
      let updtPkg = response?.updatePackage;
      if (typeof updtPkg === "string") {
        try {
          updtPkg = JSON.parse(updtPkg);
        } catch (e) {
          console.error("Error parsing updatePackage:", e);
        }
      }

      // Table1 contains the updated profile details from backend SP
      if (updtPkg && updtPkg.Table1 && updtPkg.Table1.length > 0) {
        const data = updtPkg.Table1[0];
        setProfileData(data);
        const sellsOfferings = data.Sells || data.WhatDoYouSell || "";
        const matchedAgencyID = data.AgencyID || paramM;
        const customer = getCustomerInfo(matchedAgencyID);

        setInquiryForm((prev) => ({
          ...prev,
          product: prev.product || sellsOfferings,
          name: prev.name || customer?.name || "",
          mobile: prev.mobile || customer?.mobile || "",
          cc: prev.cc && prev.cc !== "91" ? prev.cc : (customer?.cc || prev.cc || "91"),
          company: prev.company || customer?.company || "",
          email: prev.email || customer?.email || "",
        }));

        setOfferingInquiryForm((prev) => ({
          ...prev,
          name: prev.name || customer?.name || "",
          mobile: prev.mobile || customer?.mobile || "",
          company: prev.company || customer?.company || "",
          email: prev.email || customer?.email || "",
          cc: prev.cc && prev.cc !== "91" ? prev.cc : (customer?.cc || prev.cc || "91"),
        }));

        setContactForm((prev) => ({
          ...prev,
          name: prev.name || customer?.name || "",
          mobile: prev.mobile || customer?.mobile || "",
          company: prev.company || customer?.company || "",
          email: prev.email || customer?.email || "",
          cc: prev.cc && prev.cc !== "91" ? prev.cc : (customer?.cc || prev.cc || "91"),
        }));
      } else if (updtPkg && updtPkg.Table && updtPkg.Table.length > 0 && updtPkg.Table[0]?.FnErrCode !== "-1") {
        setErrorMsg(updtPkg.Table[0]?.ErrMsg || "Agency profile details not found.");
      } else {
        setErrorMsg("Agency profile not found for the specified parameter.");
      }

      // Extract catalog from updatePackage if returned by backend SP
      if (updtPkg) {
        const catalogInPkg =
          updtPkg.Catalog ||
          updtPkg.catalog ||
          updtPkg.Table1?.[0]?.Catalog ||
          updtPkg.Table1?.[0]?.catalog;
        if (catalogInPkg) {
          setCatalogFromAPI(catalogInPkg);
        }
      }
    } catch (error) {
      console.error("Error fetching webstore details:", error);
      setErrorMsg("Failed to load business profile. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!paramM) {
      setLoading(false);
      setErrorMsg("Please provide a valid agency ID or mobile number in the URL parameter.");
      return;
    }

    if (hasKnownUserOnMount.current) {
      // Known user with Jarvis or LeadInfo: fetch profile immediately
      fetchCompanyProfile();
    } else {
      // Unknown visitor: do not fetch profile yet; skeleton is displayed behind open contact drawer
      setLoading(false);
    }
  }, [paramM]);

  const getVal = (key) => {
    let val = profileData?.[key];
    if (key === "LinkedIn" && (val === undefined || val === null)) {
      val = profileData?.["LinkedIN"];
    }
    if (val === null || val === undefined) {
      return "";
    }
    return String(val).trim();
  };

  // Resolve logo or banner URL
  const getResolvedUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return getUploadUrl(`Uploads/${path}`);
  };

  // Extract all details safely
  const companyName = getVal("AgencyName") || "Business Profile";
  const welcomeAgencyName = profileData?.AgencyName || (companyName !== "Business Profile" ? companyName : "") || "";
  const welcomeTitle = welcomeAgencyName ? `Welcome to ${welcomeAgencyName}` : `Enter Our Webstore`;
  const tagline = getVal("Tagline");
  const description = getVal("Description");
  const city = getVal("City");
  const state = getVal("State");
  const address = getVal("Address");
  const pincode = getVal("Pincode");
  const cin = getVal("CIN");
  const taxId = getVal("TaxID");
  const email = getVal("Email");
  const mobile = getVal("Mobile");
  const sells = getVal("Sells") || getVal("WhatDoYouSell");
  const segment = getVal("Segment");
  const locLink = getVal("LocLink");
  const logo = getVal("Logo");

  // Lookup Segment & Industry names from SegmentID & IndustryID
  const segmentId = Number(getVal("SegmentID") || 0);
  const industryId = Number(getVal("IndustryID") || 0);

  let resolvedSegment = "";
  let resolvedIndustry = "";

  if (segmentId) {
    const segMatch = segments.find((s) => s.SegmentID === segmentId);
    if (segMatch) {
      resolvedSegment = segMatch.Segment;
      resolvedIndustry = segMatch.Industry || "";
    }
  }

  if (industryId && !resolvedIndustry) {
    const indMatch = industries.find((i) => i.IndustryID === industryId);
    if (indMatch) {
      resolvedIndustry = indMatch.Industry;
    }
  }

  const displaySegment = resolvedSegment || segment;
  const displayIndustry = resolvedIndustry;

  // Dynamically compute display tagline based on location if it matches a default pattern
  const getDisplayTagline = () => {
    if (!tagline) return "";

    const basedInIndex = tagline.indexOf(" based in ");
    if (basedInIndex !== -1) {
      const prefix = tagline.substring(0, basedInIndex).trim();

      const rawTypes = getVal("BizTypes") || getVal("BusinessTypes") || "";
      const businessTypes = rawTypes ? rawTypes.split("").map(t => t.trim()) : [];
      const businessTypeOptions = [
        { id: "M", label: "Manufacturer" },
        { id: "T", label: "Trader" },
        { id: "R", label: "Retailer" },
        { id: "S", label: "Service Provider" },
      ];
      const businessTypeStr = businessTypes.length > 0
        ? businessTypes.length === 1
          ? (businessTypeOptions.find((option) => option.id === businessTypes[0])?.label || businessTypes[0])
          : "Supplier"
        : "";

      const possiblePrefixes = [];
      if (displaySegment && displayIndustry) {
        const segStr = `of ${displaySegment} (${displayIndustry})`;
        if (businessTypeStr) {
          possiblePrefixes.push(`${businessTypeStr} ${segStr}`);
        }
        possiblePrefixes.push(`Supplier ${segStr}`);
      }
      if (businessTypeStr) {
        possiblePrefixes.push(businessTypeStr);
      }

      const isDefault = possiblePrefixes.some(p => typeof p === "string" && p.toLowerCase() === prefix.toLowerCase());

      if (isDefault || tagline.toLowerCase().includes("based in ahmedabad, gujarat")) {
        const locationPart = (city && state) ? `${city}, ${state}` : (city || state || "");
        if (locationPart) {
          return `${prefix} based in ${locationPart}`;
        } else {
          return prefix;
        }
      }
    }

    return tagline;
  };

  const displayTagline = getDisplayTagline();

  // Dynamically update document title and SEO/Social Share Meta Tags (OpenGraph, Twitter)
  useEffect(() => {
    if (!profileData) return;

    const shareTitle = `${companyName} | Webstore on SahiClub`;
    const shareDesc = displayTagline || sells || description || `Explore ${companyName} products, services, and business profile on SahiClub.`;
    const shareLogo = logo ? getResolvedUrl(logo) : `${window.location.origin}${getImageRoot('Images', 'SahiClub_BG.png')}`;

    // 1. Update Document Title
    document.title = shareTitle;

    // 2. Helper function to set or create meta tags dynamically
    const setMetaTag = (selector, attributeName, attributeValue, content) => {
      if (!content) return;
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attributeName, attributeValue);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Standard SEO Meta Tags
    setMetaTag('meta[name="description"]', 'name', 'description', shareDesc);

    // OpenGraph Meta Tags (WhatsApp, Facebook, LinkedIn, Slack)
    setMetaTag('meta[property="og:title"]', 'property', 'og:title', shareTitle);
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', shareDesc);
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', window.location.href);
    setMetaTag('meta[property="og:image"]', 'property', 'og:image', shareLogo);
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', 'website');
    setMetaTag('meta[property="og:site_name"]', 'property', 'og:site_name', 'SahiClub');

    // Twitter Card Meta Tags
    setMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', shareTitle);
    setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', shareDesc);
    setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', shareLogo);
  }, [profileData, companyName, displayTagline, sells, description, logo]);

  // Parse Business Types
  const getBizTypesArray = () => {
    const rawTypes = getVal("BizTypes") || getVal("BusinessTypes");
    if (!rawTypes) return [];
    const codeMap = {
      M: "Manufacturer",
      T: "Trader",
      R: "Retailer",
      S: "Service Provider",
    };

    if (typeof rawTypes === "string") {
      return rawTypes
        .split("")
        .map((char) => codeMap[char.toUpperCase()])
        .filter(Boolean);
    }
    return Array.isArray(rawTypes) ? rawTypes : [];
  };

  const businessTypes = getBizTypesArray();
  const rawMobileClean = mobile ? mobile.replace(/[^0-9]/g, "") : "";
  const defaultWaMessage = `Hi ${companyName || "there"}, I came across your Webstore on SahiClub and have an inquiry.`;
  const whatsappUrl = rawMobileClean ? `https://wa.me/91${rawMobileClean.slice(-10)}?text=${encodeURIComponent(defaultWaMessage)}` : "";

  const handleShare = async () => {
    const shareTitle = companyName || "Business Profile";
    const shareUrl = paramM ? `https://sahi.club/web/store?m=${paramM}` : window.location.href.replace('/web/webstore', '/web/store');
    const shareText = `*${shareTitle}* is now on *Sahi Club*!\nYou can connect with us instantly and post any inquiry on our Sahi Club web store.\n${shareUrl}`;
    const imgToShare = logo ? getResolvedUrl(logo) : getImageRoot('Images', 'SahiClub_BG.png');

    await shareWithImage({
      title: shareTitle,
      text: shareText,
      url: shareUrl,
      imageUrl: imgToShare,
      imageFileName: `${(companyName || 'webstore').replace(/[^a-zA-Z0-9]/g, '_')}_profile.png`,
      successCopyMessage: "Webstore details copied to clipboard!"
    });
  };

  const handlePhoneClick = (e) => {
    // If in desktop view, copy the phone number to clipboard instead of opening the dialer
    const isDesktop = !isMobile || (typeof window !== "undefined" && window.innerWidth >= 1024);
    if (isDesktop) {
      e.preventDefault();
      if (mobile) {
        if (navigator?.clipboard?.writeText) {
          navigator.clipboard.writeText(mobile)
            .then(() => toast.success("Phone number copied to clipboard!"))
            .catch(() => {
              const textarea = document.createElement("textarea");
              textarea.value = mobile;
              document.body.appendChild(textarea);
              textarea.select();
              document.execCommand("copy");
              document.body.removeChild(textarea);
              toast.success("Phone number copied to clipboard!");
            });
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = mobile;
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
          toast.success("Phone number copied to clipboard!");
        }
      }
    }
  };

  const handleScrollToForm = () => {
    setIsInquiryOpen(true);

    const scrollToTarget = () => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      setTimeout(() => {
        const input = document.getElementById("webstore-needs-input");
        if (input) {
          input.focus();
        }
      }, 400);
    };

    if (!isInquiryOpen) {
      setTimeout(scrollToTarget, 60);
    } else {
      scrollToTarget();
    }
  };

  // Parse and resolve Catalog products for Webstore preview (isolated from master offerings)
  const parseCatalogProducts = () => {
    const rawData =
      (catalogFromAPI && (Array.isArray(catalogFromAPI) ? catalogFromAPI.length > 0 : catalogFromAPI.length > 2) ? catalogFromAPI : null) ||
      profileData?.Catalog ||
      profileData?.catalog ||
      getMasterDataFromConfig("Catalog") ||
      getConfigData()?.Catalog;

    if (!rawData) return [];
    let items = [];
    if (Array.isArray(rawData)) {
      items = rawData;
    } else if (typeof rawData === "string") {
      try {
        const parsed = JSON.parse(rawData);
        items = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        items = [];
      }
    }
    return items.filter((item) => !item.IsD);
  };

  const catalogProducts = parseCatalogProducts();

  // If there is no catalog for the agency, have the inquiry form uncollapsed by default (mobile and desktop)
  const hasSetDefaultInquiryOpen = useRef(false);

  useEffect(() => {
    hasSetDefaultInquiryOpen.current = false;
  }, [paramM]);

  useEffect(() => {
    if (!loading && !hasSetDefaultInquiryOpen.current) {
      if (catalogProducts.length === 0) {
        setIsInquiryOpen(true);
      }
      hasSetDefaultInquiryOpen.current = true;
    }
  }, [loading, catalogProducts.length]);

  const getOfferingImageUrl = (rawImg) => {
    if (!rawImg) return "";
    if (typeof rawImg !== "string") return "";
    const trimmed = rawImg.trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
    const cleanPath = trimmed.replace(/^\/+/, "");
    if (cleanPath.toLowerCase().startsWith("uploads/")) {
      return getUploadUrl(cleanPath);
    }
    return getUploadUrl(`Uploads/${cleanPath}`);
  };

  const handleOfferingInquirySubmit = async (e) => {
    if (e) e.preventDefault();
    if (!offeringInquiryForm.needs.trim()) {
      toast.error("Please enter requirement details.");
      return;
    }
    if (!offeringInquiryForm.name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    if (!offeringInquiryForm.mobile.trim()) {
      toast.error("Please enter mobile number.");
      return;
    }
    const mobileDigits = offeringInquiryForm.mobile.trim().replace(/\D/g, "");
    const ccDigits = (offeringInquiryForm.cc || "91").replace(/\D/g, "") || "91";
    if (ccDigits === "91" && mobileDigits.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    } else if (ccDigits !== "91" && (mobileDigits.length < 5 || mobileDigits.length > 15)) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    if (offeringInquiryForm.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(offeringInquiryForm.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }

    const offeringName = selectedOffering?.PNm || selectedOffering?.name || "";

    setSubmittingOfferingInquiry(true);
    try {
      const response = await externalAPI.sendWebstoreInquiry({
        agencyID: paramM,
        company: offeringInquiryForm.company.trim() || "-",
        name: offeringInquiryForm.name.trim(),
        mobile: mobileDigits,
        cc: ccDigits,
        email: offeringInquiryForm.email.trim(),
        product: offeringName,
        needs: offeringInquiryForm.needs.trim(),
        source: paramSrc,
        agencyName: companyName || "our Webstore",
        ownerMobile: mobile || "",
        ownerName: companyName || "",
        ownerCC: "91",
      });

      if (response?.error === false || response?.code === 200 || response?.status === "SUCCESS") {
        toast.success("Inquiry submitted! We will contact you soon.");
        saveLeadInfo({
          name: offeringInquiryForm.name.trim(),
          mobile: mobileDigits,
          cc: ccDigits,
          company: offeringInquiryForm.company.trim(),
          email: offeringInquiryForm.email.trim(),
        });
        resetOfferingModalState();
      } else {
        toast.error(response?.message || response?.status || "Failed to submit inquiry. Please try again.");
      }
    } catch (err) {
      console.error("Error submitting offering inquiry:", err);
      toast.error("Error submitting inquiry. Please try again later.");
    } finally {
      setSubmittingOfferingInquiry(false);
    }
  };

  const handleContactSubmit = async (e) => {
    if (e) e.preventDefault();
    const name = (contactForm?.name || "").trim();
    const mobileDigits = (contactForm?.mobile || "").trim().replace(/\D/g, "");
    const ccDigits = (contactForm?.cc || "91").replace(/\D/g, "") || "91";
    const company = (contactForm?.company || "").trim();
    const email = (contactForm?.email || "").trim();

    if (!name) {
      toast.error("Please enter your name.");
      return;
    }
    if (!mobileDigits) {
      toast.error("Please enter mobile number.");
      return;
    }
    if (ccDigits === "91" && mobileDigits.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    } else if (ccDigits !== "91" && (mobileDigits.length < 5 || mobileDigits.length > 15)) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setSubmittingContactDetails(true);
    try {
      // 1. Persist contact details to LeadInfo cookie (365 days) and localStorage
      saveLeadInfo({
        name,
        mobile: mobileDigits,
        cc: ccDigits,
        company,
        email,
      });

      // 2. Synchronize all inquiry form states so future inquiries are prefilled
      setOfferingInquiryForm((prev) => ({
        ...prev,
        name,
        mobile: mobileDigits,
        cc: ccDigits,
        company,
        email,
      }));
      setInquiryForm((prev) => ({
        ...prev,
        name,
        mobile: mobileDigits,
        cc: ccDigits,
        company,
        email,
      }));

      // 3. Fetch webstore details and dispatch visit alert
      await fetchCompanyProfile({
        name,
        mobile: mobileDigits,
        cc: ccDigits,
        company,
        email,
        changeType: "VISIT",
      });

      toast.success("Welcome to our Webstore!");
      setIsContactPromptOpen(false);
    } catch (err) {
      console.error("Error saving contact details:", err);
      toast.error("Failed to save contact details. Please try again.");
    } finally {
      setSubmittingContactDetails(false);
    }
  };

  const handleInquirySubmit = async (e) => {
    if (e) e.preventDefault();
    if (!inquiryForm.needs.trim()) {
      toast.error("Please enter requirement details.");
      return;
    }
    if (!inquiryForm.name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    const mobileDigits = inquiryForm.mobile.trim().replace(/\D/g, "");
    const ccDigits = (inquiryForm.cc || "91").replace(/\D/g, "") || "91";
    if (ccDigits === "91" && mobileDigits.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    } else if (ccDigits !== "91" && (mobileDigits.length < 5 || mobileDigits.length > 15)) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    if (inquiryForm.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inquiryForm.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setSubmittingInquiry(true);
    try {
      const response = await externalAPI.sendWebstoreInquiry({
        agencyID: paramM,
        company: inquiryForm.company.trim() || "-",
        name: inquiryForm.name.trim(),
        mobile: mobileDigits,
        cc: ccDigits,
        email: inquiryForm.email.trim(),
        product: "",
        needs: inquiryForm.needs.trim(),
        source: paramSrc,
        agencyName: companyName || "our Webstore",
        ownerMobile: mobile || "",
        ownerName: companyName || "",
        ownerCC: "91",
      });

      if (response?.error === false || response?.code === 200 || response?.status === "SUCCESS") {
        toast.success("Inquiry submitted! We will contact you soon.");
        saveLeadInfo({
          name: inquiryForm.name.trim(),
          mobile: mobileDigits,
          cc: ccDigits,
          company: inquiryForm.company.trim(),
          email: inquiryForm.email.trim(),
        });
        setIsDirectInquiryContactDrawerOpen(false);
        const currentAgencyID = profileData?.AgencyID || paramM;
        const customer = getCustomerInfo(currentAgencyID);
        setInquiryForm({
          company: customer?.company || "",
          name: customer?.name || "",
          mobile: customer?.mobile || "",
          cc: customer?.cc || "91",
          email: customer?.email || "",
          product: sells || "",
          needs: "",
        });
      } else {
        toast.error(response?.message || response?.status || "Failed to submit inquiry. Please try again.");
      }
    } catch (err) {
      console.error("Error submitting inquiry:", err);
      toast.error("Error submitting inquiry. Please try again later.");
    } finally {
      setSubmittingInquiry(false);
    }
  };

  if (errorMsg) {
    return (
      <div className="min-h-screen bg-[var(--bg-grey)] flex flex-col items-center selection:bg-[var(--sahi-green-light)] selection:text-[var(--sahi-green)] relative">
        {/* Top SahiClub Header Bar for mobile iframe/app view (when paramI === "1") */}
        {showBackButton && isMobile && (
          <header className="fixed top-0 text-black h-16 flex items-center lg:hidden justify-center z-50 left-0 right-0 bg-white">
            <div className="flex items-center justify-between w-full px-4 h-16 bg-white border-b border-sahi-border-color">
              <img
                src={
                  isLive()
                    ? "/Web/assets/Images/SahiLogo.png"
                    : "/assets/Images/SahiLogo.png"
                }
                alt="SahiClub Logo"
                className="h-7 md:h-9 cursor-pointer"
                onClick={handleLogoClick}
              />
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  onClick={() => setIsMenuDrawerOpen(true)}
                >
                  <AlignJustify className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </header>
        )}
        <div className={`flex-1 flex flex-col items-center justify-center p-4 w-full ${showBackButton && isMobile ? "pt-20" : ""}`}>
          <div className="flex flex-col items-center max-w-lg w-full text-center space-y-5">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[var(--sahi-green-light)] flex items-center justify-center text-[var(--sahi-green)]">
              <AlertCircle className="h-8 w-8 md:h-10 md:w-10" />
            </div>
            <h3 className="font-bold text-[var(--text-color)] text-xl sm:text-2xl md:text-3xl">Webstore Profile Unavailable</h3>
            <p className="text-sm md:text-base text-[var(--sub-text-color)] leading-relaxed max-w-md">
              {errorMsg || "Unable to load business details for the requested agency."}
            </p>
            <div className="flex flex-col items-center justify-center gap-3 pt-1">
              <Button
                onClick={() => window.location.reload()}
                className="w-40 h-11 bg-[var(--sahi-green)] text-white text-sm md:text-base font-semibold rounded-md hover:bg-[#084516] transition-colors cursor-pointer shadow-xs"
              >
                Retry
              </Button>
              {showBackButton && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleBack}
                  className="w-40 h-11 sahi-secondary-button flex items-center justify-center gap-2 text-sm md:text-base font-semibold rounded-md cursor-pointer shadow-xs"
                >
                  <ArrowLeft className="h-4 w-4 shrink-0" />
                  <span>Go back</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasSidebar = Boolean(
    businessTypes.length > 0 ||
    displayIndustry ||
    displaySegment ||
    city ||
    state ||
    taxId ||
    cin ||
    address ||
    locLink ||
    email ||
    mobile ||
    description
  );

  return (
    <div className="min-h-screen bg-[var(--bg-grey)] text-[var(--text-color)] font-['Inter',sans-serif] selection:bg-[var(--sahi-green-light)] selection:text-[var(--sahi-green)] flex flex-col items-center relative overflow-x-hidden">
      {/* Top SahiClub Header Bar for mobile iframe/app view (when paramI === "1") */}
      {showBackButton && isMobile && (
        <header className="fixed top-0 text-black h-16 flex items-center lg:hidden justify-center z-50 left-0 right-0 bg-white">
          <div className="flex items-center justify-between w-full px-4 h-16 bg-white border-b border-sahi-border-color">
            <img
              src={
                isLive()
                  ? "/Web/assets/Images/SahiLogo.png"
                  : "/assets/Images/SahiLogo.png"
              }
              alt="SahiClub Logo"
              className="h-7 md:h-9 cursor-pointer"
              onClick={handleLogoClick}
            />
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                type="button"
                onClick={() => setIsMenuDrawerOpen(true)}
              >
                <AlignJustify className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>
      )}

      <div className={`w-full flex-1 flex flex-col items-center py-4 sm:py-6 md:py-8 px-3 sm:px-6 md:px-8 ${showBackButton && isMobile ? "pt-20" : ""}`}>
        {!profileData ? (
          <WebStoreSkeleton />
        ) : (
          <div className="w-full max-w-7xl space-y-4 sm:space-y-6 lg:pb-8 relative animate-in fade-in duration-300">
            {/* Full-Width Top Header Card */}
            <div className="bg-white border border-[var(--border)] rounded-2xl p-5 sm:p-6 lg:p-7 shadow-[0_2px_10px_rgba(0,0,0,0.04)] relative overflow-hidden flex flex-col lg:flex-row items-center justify-between gap-5 sm:gap-6 lg:gap-8">

              {/* Brand Row: Logo on Left, Name & Tagline on Right */}
              <div className="flex flex-row items-start justify-start gap-3.5 sm:gap-4 lg:gap-6 w-full lg:w-auto min-w-0">
                {/* Logo Badge */}
                {logo && !logoError && (
                  <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-18 lg:h-18 rounded-xl sm:rounded-2xl border border-[var(--border)] bg-white overflow-hidden flex items-center justify-center shrink-0">
                    <img
                      src={getResolvedUrl(logo)}
                      alt={companyName || "Business Logo"}
                      className="w-full h-full object-contain"
                      onError={() => setLogoError(true)}
                    />
                  </div>
                )}

                {/* Name and Tagline stacked up and down */}
                <div className="flex flex-col justify-start items-start text-left min-w-0 flex-1">
                  <h1 className="text-2xl sm:text-2xl lg:text-2xl xl:text-3xl font-bold text-[var(--text-color)] tracking-tight leading-tight">
                    {companyName}
                  </h1>

                  {displayTagline && (
                    <p className="text-sm sm:text-sm lg:text-base xl:text-base text-[var(--sub-text-color)] font-normal leading-normal mt-1 max-w-xl text-left">
                      {displayTagline}
                    </p>
                  )}
                </div>
              </div>

              {/* Action Icons Row (Shifted to right on desktop, Centered with dynamic fluid spacing on mobile) */}
              <div className="flex items-center justify-between sm:justify-around lg:justify-end gap-2 sm:gap-4 lg:gap-4 xl:gap-5 w-full max-w-sm sm:max-w-md lg:max-w-none lg:w-auto mx-auto lg:mx-0 py-3 lg:py-0 border-[var(--border)]/60 shrink-0 px-2 sm:px-4 lg:px-0">
                {/* 1. Inquire (Sahi Orange Light bg, Sahi Orange text) */}
                <button
                  type="button"
                  onClick={handleScrollToForm}
                  className="flex-1 lg:flex-initial flex flex-col items-center gap-1.5 group cursor-pointer outline-none bg-transparent border-none p-0 max-w-[72px] sm:max-w-[80px] lg:max-w-none"
                  title="Post Inquiry"
                >
                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-sahi-orange-light hover:opacity-80 text-sahi-orange flex items-center justify-center shadow-xs transition-all group-active:scale-95 group-hover:shadow-sm">
                    <Send className="h-5 w-5 text-sahi-orange" />
                  </div>
                  <span className="text-xs font-medium text-[var(--sub-text-color)] group-hover:text-[var(--text-color)] transition-colors">
                    Inquire
                  </span>
                </button>

                {/* 2. Call */}
                {mobile && (
                  <a
                    href={`tel:${mobile}`}
                    onClick={handlePhoneClick}
                    className="flex-1 lg:flex-initial flex flex-col items-center gap-1.5 group cursor-pointer outline-none no-underline max-w-[72px] sm:max-w-[80px] lg:max-w-none"
                    title={!isMobile ? "Copy phone number" : "Call Business"}
                  >
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-sahi-blue-light hover:opacity-90 text-sahi-dark-blue flex items-center justify-center transition-all group-active:scale-95 group-hover:shadow-sm">
                      <Phone className="h-5 w-5 text-sahi-dark-blue" />
                    </div>
                    <span className="text-xs font-medium text-[var(--sub-text-color)] group-hover:text-[var(--text-color)] transition-colors">
                      Call
                    </span>
                  </a>
                )}

                {/* 3. WhatsApp (WhatsApp Green bg, Sahi Green text) */}
                {whatsappUrl && (
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 lg:flex-initial flex flex-col items-center gap-1.5 group cursor-pointer outline-none no-underline max-w-[72px] sm:max-w-[80px] lg:max-w-none"
                    title="WhatsApp"
                  >
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[var(--sahi-green-light)] text-[var(--sahi-green)] flex items-center justify-center transition-all group-active:scale-95 group-hover:shadow-sm">
                      <BsWhatsapp className="h-5 w-5 text-[var(--sahi-green)]" />
                    </div>
                    <span className="text-xs font-medium text-[var(--sub-text-color)] group-hover:text-[var(--text-color)] transition-colors">
                      WhatsApp
                    </span>
                  </a>
                )}

                {/* 4. Share (Light Gray) */}
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex-1 lg:flex-initial flex flex-col items-center gap-1.5 group cursor-pointer outline-none bg-transparent border-none p-0 max-w-[72px] sm:max-w-[80px] lg:max-w-none"
                  title="Share Webstore"
                >
                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gray-200 text-[var(--bg-grey)] border border-zinc-200 flex items-center justify-center transition-all group-active:scale-95">
                    <Share2 className="h-5 w-5 text-zinc-700" />
                  </div>
                  <span className="text-xs font-medium text-[var(--sub-text-color)] group-hover:text-[var(--text-color)] transition-colors">
                    Share
                  </span>
                </button>
              </div>
            </div>

            {/* 2-Column Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 items-start">

              {/* LEFT COLUMN: Offerings Section + Inquiry Form */}
              <div className={`${hasSidebar ? "lg:col-span-8" : "lg:col-span-12"} space-y-4 sm:space-y-6`}>

                {/* Catalog Section (Shown above Your Inquiry) */}
                {catalogProducts.length > 0 && (
                  <div className="bg-white border border-[var(--border)] rounded-xl p-4 sm:p-6 md:p-7 shadow-xs space-y-4">
                    <div
                      onClick={() => {
                        if (isMobile && catalogProducts.length > 4) {
                          if (!showAllCatalogMobile) {
                            shouldScrollToNewProducts.current = true;
                            setShowAllCatalogMobile(true);
                          } else {
                            setShowAllCatalogMobile(false);
                          }
                        }
                      }}
                      className={`pb-3 md:pb-4 flex items-center justify-between ${isMobile && catalogProducts.length > 4 ? "cursor-pointer select-none" : ""
                        }`}
                    >
                      <div>
                        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-[var(--text-color)]">
                          Catalog
                        </h2>
                        <p className="text-xs sm:text-sm text-[var(--sub-text-color)] mt-1">
                          Click on an offering to inquire.
                        </p>
                      </div>
                      {isMobile && catalogProducts.length > 4 ? (
                        <div
                          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900 shadow-2xs transition-all duration-200 active:scale-95 select-none"
                          title={showAllCatalogMobile ? "Hide additional products" : "See more catalog products"}
                        >
                          {showAllCatalogMobile ? (
                            <>
                              <ChevronUp className="h-3.5 w-3.5" />
                            </>
                          ) : (
                            <>
                              <span className="text-xs">See All</span>
                              <ChevronDown className="h-3.5 w-3.5" />
                            </>
                          )}
                        </div>
                      ) : null}
                    </div>

                    {/* Catalog Grid (2 columns on mobile max 4 items unless expanded, 5 columns on desktop) */}
                    {(() => {
                      const visibleProducts = isMobile && !showAllCatalogMobile
                        ? catalogProducts.slice(0, 4)
                        : catalogProducts;

                      return (
                        <>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3.5 sm:gap-4 !mt-0">
                            {visibleProducts.map((item, idx) => {
                              const name = item.PNm || item.name || "Product";
                              const rawImg = item.PImg || item.img || item.imageUrl || item.image || item.Img;
                              const imgUrl = getOfferingImageUrl(rawImg);

                              return (
                                <div
                                  key={item.PID || idx}
                                  ref={idx === 4 ? firstNewlyVisibleRef : null}
                                  className="flex flex-col cursor-pointer scroll-mt-6 group"
                                  onClick={() => {
                                    setSelectedOffering(item);
                                    setMobileInquiryStep(1);
                                    const currentAgencyID = profileData?.AgencyID || paramM;
                                    const customer = getCustomerInfo(currentAgencyID);
                                    setOfferingInquiryForm((prev) => ({
                                      company: prev.company || customer?.company || "",
                                      name: prev.name || customer?.name || "",
                                      mobile: prev.mobile || customer?.mobile || "",
                                      cc: prev.cc || customer?.cc || "91",
                                      email: prev.email || customer?.email || "",
                                      needs: "",
                                    }));
                                  }}
                                >
                                  {/* Square Image Thumbnail (Hover effect restricted to image only) */}
                                  <div
                                    className="relative aspect-square w-full rounded-xl overflow-hidden bg-zinc-100 flex items-center justify-center border border-[var(--border)] group hover:border-[var(--sahi-green)]/40 shadow-[0_0_14px_rgba(0,0,0,0.12)] hover:shadow-[0_0_18px_rgba(0,0,0,0.18)] transition-all"
                                    title="Click to inquire"
                                  >
                                    {imgUrl ? (
                                      <img
                                        src={imgUrl}
                                        alt={name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        onError={(e) => {
                                          e.target.style.display = "none";
                                          if (e.target.nextElementSibling) {
                                            e.target.nextElementSibling.style.display = "flex";
                                          }
                                        }}
                                      />
                                    ) : null}
                                    <div className={`${imgUrl ? "hidden" : "flex"} absolute inset-0 items-center justify-center bg-zinc-100 text-zinc-400`}>
                                      <ImageIcon className="w-6 h-6 opacity-40" />
                                    </div>
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors pointer-events-none" />
                                  </div>

                                  {/* Catalog Item Details (Centered Name) - Hidden in mobile view */}
                                  <div className="hidden sm:flex pt-2 pb-1 px-1 flex-col items-center text-center w-full">
                                    <h3
                                      className="font-semibold text-xs sm:text-sm text-[var(--text-color)] group-hover:text-[var(--sahi-green)] transition-colors truncate w-full text-center"
                                      title={name}
                                    >
                                      {name}
                                    </h3>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Embedded Inquiry Form (Collapsible, Collapsed by Default) */}
                <div
                  ref={formRef}
                  className="bg-white border border-[var(--border)] hover:border-[var(--sahi-green)]/30 rounded-xl p-4 sm:p-6 md:p-7 shadow-xs hover:shadow-sm transition-all scroll-mt-6"
                >
                  <div
                    onClick={() => setIsInquiryOpen((prev) => !prev)}
                    className={`flex items-center justify-between cursor-pointer select-none group`}
                  >
                    <div>
                      <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-[var(--text-color)] group-hover:text-[var(--sahi-green)] transition-colors">
                        Inquiry
                      </h2>
                      <p className="text-xs sm:text-sm text-[var(--sub-text-color)] mt-1">
                        Enter your requirement and we will get back to you.
                      </p>
                    </div>
                    <ChevronDown className={`h-5 w-5 text-[var(--sub-text-color)] transition-transform duration-200 shrink-0 ml-2 ${isInquiryOpen ? "rotate-180" : ""}`} />
                  </div>

                  {isInquiryOpen && (
                    isMobile ? (
                      /* Mobile Direct Inquiry: ONLY Requirement field + Submit Inquiry button */
                      <form
                        noValidate
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (!inquiryForm.needs.trim()) {
                            toast.error("Please enter requirement details.");
                            return;
                          }
                          const currentAgencyID = profileData?.AgencyID || paramM;
                          const customer = getCustomerInfo(currentAgencyID);
                          setInquiryForm((prev) => ({
                            ...prev,
                            name: prev.name || customer?.name || "",
                            mobile: prev.mobile || customer?.mobile || "",
                            cc: prev.cc || customer?.cc || "91",
                            company: prev.company || customer?.company || "",
                            email: prev.email || customer?.email || "",
                          }));
                          setIsDirectInquiryContactDrawerOpen(true);
                        }}
                        className="space-y-3.5 mt-2.5"
                      >
                        {/* Field 1: Requirement (Only field shown on mobile) */}
                        <div className="space-y-1.5 text-left">
                          <Label className="text-sm font-medium text-[var(--text-color)]">
                            Requirement <span className="text-red-500 font-bold">*</span>
                          </Label>
                          <div className="relative">
                            <Textarea
                              id="webstore-needs-input"
                              rows={3}
                              required
                              placeholder="e.g. quantity, specifications..."
                              value={inquiryForm.needs}
                              onChange={(e) => setInquiryForm((prev) => ({ ...prev, needs: e.target.value }))}
                              className="text-sm sm:text-base text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg min-h-[90px] resize-y pr-14"
                            />
                            <SpeechControls
                              targetId="webstore-needs-input"
                              value={inquiryForm.needs}
                              onChange={(val) => setInquiryForm((prev) => ({ ...prev, needs: val }))}
                              isMobile={isMobile}
                              className="absolute right-2 top-2"
                            />
                          </div>
                        </div>

                        <div className="pt-1">
                          <Button
                            type="submit"
                            variant="sahiGreen"
                            className="w-full h-11 flex items-center justify-center gap-2 font-semibold text-sm cursor-pointer rounded-lg shadow-xs"
                          >
                            <Send className="h-4 w-4" /> Submit Inquiry
                          </Button>
                        </div>
                      </form>
                    ) : (
                      /* Desktop Direct Inquiry: Keep full form inline as before */
                      <form noValidate onSubmit={handleInquirySubmit} className="space-y-3.5 md:space-y-4 mt-2.5">
                        {/* Field 1: Requirement (Top Compulsory Field) */}
                        <div className="space-y-1 md:space-y-1.5 text-left">
                          <Label className="text-sm font-medium text-[var(--text-color)]">
                            Requirement <span className="text-red-500 font-bold">*</span>
                          </Label>
                          <div className="relative">
                            <Textarea
                              id="webstore-needs-input"
                              rows={3}
                              required
                              placeholder="e.g. quantity, specifications..."
                              value={inquiryForm.needs}
                              onChange={(e) => setInquiryForm((prev) => ({ ...prev, needs: e.target.value }))}
                              className="text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-md min-h-[90px] sm:min-h-[150px] resize-y pr-14"
                            />
                            <SpeechControls
                              targetId="webstore-needs-input"
                              value={inquiryForm.needs}
                              onChange={(val) => setInquiryForm((prev) => ({ ...prev, needs: val }))}
                              isMobile={isMobile}
                              className="absolute right-2 top-2"
                            />
                          </div>
                        </div>

                        {/* Row 2: Company Name & Your Name */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 md:gap-4">
                          <div className="space-y-1 md:space-y-1.5 text-left">
                            <Label className="text-sm font-medium text-[var(--text-color)]">Company Name</Label>
                            <Input
                              type="text"
                              placeholder="e.g. Acme Corp"
                              value={inquiryForm.company}
                              onChange={(e) => setInquiryForm((prev) => ({ ...prev, company: e.target.value }))}
                              className="h-10 md:h-11 text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-md"
                            />
                          </div>

                          <div className="space-y-1 md:space-y-1.5 text-left">
                            <Label className="text-sm font-medium text-[var(--text-color)]">
                              Your Name <span className="text-red-500 font-bold">*</span>
                            </Label>
                            <Input
                              type="text"
                              required
                              placeholder="e.g. John Doe"
                              value={inquiryForm.name}
                              onChange={(e) => setInquiryForm((prev) => ({ ...prev, name: e.target.value }))}
                              className="h-10 md:h-11 text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-md"
                            />
                          </div>
                        </div>

                        {/* Row 3: Mobile Number & Email Address */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 md:gap-4">
                          <div className="space-y-1 md:space-y-1.5 text-left">
                            <Label className="text-sm font-medium text-[var(--text-color)]">
                              Mobile Number <span className="text-red-500 font-bold">*</span>
                            </Label>
                            <MobileInput
                              value={inquiryForm.mobile}
                              onChange={(e) => {
                                const val = e.target.value.replace(/[^0-9]/g, "");
                                setInquiryForm((prev) => ({ ...prev, mobile: val }));
                              }}
                              selectedCountryCode={inquiryForm.cc || "91"}
                              onCountryCodeChange={(newCC) => {
                                setInquiryForm((prev) => ({ ...prev, cc: newCC }));
                              }}
                              placeholder="e.g. 9876543210"
                              className="w-full"
                              containerClassName="h-10 md:h-11 rounded-md"
                              inputClassName="text-sm"
                            />
                          </div>

                          <div className="space-y-1 md:space-y-1.5 text-left">
                            <Label className="text-sm font-medium text-[var(--text-color)]">Email Address</Label>
                            <Input
                              type="email"
                              placeholder="e.g. john@example.com"
                              value={inquiryForm.email}
                              onChange={(e) => setInquiryForm((prev) => ({ ...prev, email: e.target.value }))}
                              className="h-10 md:h-11 text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-md"
                            />
                          </div>
                        </div>

                        <div className="pt-2">
                          <Button
                            type="submit"
                            variant="sahiGreen"
                            disabled={submittingInquiry}
                            className="w-full sm:w-auto min-w-[150px] px-6 h-10 md:h-11 flex items-center justify-center gap-2 font-semibold text-sm cursor-pointer"
                          >
                            {submittingInquiry ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" /> Submitting Inquiry...
                              </>
                            ) : (
                              <>
                                <Send className="h-4 w-4" /> Submit Inquiry
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    )
                  )}
                </div>
              </div>

              {/* RIGHT SIDEBAR: Business Details Cards Stack */}
              {hasSidebar && (
                <div className="lg:col-span-4 space-y-4 md:space-y-5">

                  {/* Card 1: Business Overview Information */}
                  {(businessTypes.length > 0 || sells || displayIndustry || displaySegment || city || state || taxId || cin) && (
                    <div className="bg-white border border-[var(--border)] rounded-xl p-4 sm:p-5 md:p-6 shadow-xs">
                      <h2 className="text-base sm:text-lg md:text-base font-bold text-[var(--text-color)] mb-2.5">
                        Business Background
                      </h2>
                      <div className="space-y-3.5 sm:space-y-4">

                        {/* Business Type */}
                        {businessTypes.length > 0 && (
                          <div className="flex items-start gap-3.5">
                            <div className="w-9 h-9 md:w-11 md:h-11 rounded-lg bg-[var(--sahi-green-light)] text-[var(--sahi-green)] flex items-center justify-center shrink-0">
                              <Briefcase className="h-5 w-5" />
                            </div>
                            <div>
                              <div className="text-xs font-semibold text-[var(--sub-text-color)]">Business Type</div>
                              <div className="text-sm font-medium text-[var(--text-color)] mt-0.5">{businessTypes.join(", ")}</div>
                            </div>
                          </div>
                        )}

                        {/* Industry & Segment */}
                        {(displayIndustry || displaySegment) && (
                          <div className="flex items-start gap-3.5">
                            <div className="w-9 h-9 md:w-11 md:h-11 rounded-lg bg-[var(--sahi-green-light)] text-[var(--sahi-green)] flex items-center justify-center shrink-0">
                              <Globe className="h-5 w-5" />
                            </div>
                            <div>
                              <div className="text-xs font-semibold text-[var(--sub-text-color)]">Industry & Segment</div>
                              <div className="text-sm font-medium text-[var(--text-color)] mt-0.5 break-words">
                                {[displayIndustry, displaySegment].filter(Boolean).join(" > ")}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Location */}
                        {(city || state || locLink) && (
                          <div className="flex items-start gap-3.5">
                            <div className="w-9 h-9 md:w-11 md:h-11 rounded-lg bg-[var(--sahi-green-light)] text-[var(--sahi-green)] flex items-center justify-center shrink-0">
                              <MapPin className="h-5 w-5" />
                            </div>
                            <div>
                              <div className="text-xs font-semibold text-[var(--sub-text-color)]">Location</div>
                              <div className="text-sm font-medium text-[var(--text-color)] mt-0.5">
                                {locLink ? (
                                  <a
                                    href={locLink.startsWith("http") ? locLink : `https://${locLink}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 text-[var(--text-color)] hover:text-[var(--sahi-green)] transition-colors group/loc"
                                    title="Get Directions"
                                  >
                                    <span>{[city, state].filter(Boolean).join(", ") || "View on Map"}</span>
                                    <ExternalLink className="h-3.5 w-3.5 text-[var(--sub-text-color)] group-hover/loc:text-[var(--sahi-green)] transition-colors shrink-0" />
                                  </a>
                                ) : (
                                  <span>{[city, state].filter(Boolean).join(", ")}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* GSTIN / Tax ID / CIN */}
                        {(taxId || cin) && (
                          <div className="flex items-start gap-3.5">
                            <div className="w-9 h-9 md:w-11 md:h-11 rounded-lg bg-[var(--sahi-green-light)] text-[var(--sahi-green)] flex items-center justify-center shrink-0">
                              <span className="font-bold text-xs md:text-sm leading-none">#</span>
                            </div>
                            <div>
                              <div className="text-xs font-semibold text-[var(--sub-text-color)]">GSTIN / Tax Registration</div>
                              <div className="text-sm font-mono font-medium text-[var(--text-color)] mt-0.5">
                                {taxId || cin}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Card 2: Contact Information */}
                  {(email || mobile) && (
                    <div className="bg-white border border-[var(--border)] hover:border-[var(--sahi-green)]/30 rounded-xl p-4 sm:p-5 md:p-6 shadow-xs hover:shadow-sm transition-all">
                      <h2 className="text-base sm:text-lg md:text-base font-bold text-[var(--text-color)] mb-2.5">
                        Contact Us
                      </h2>
                      <div className="space-y-3 text-sm">
                        {email && (
                          <a
                            href={`mailto:${email}`}
                            className="flex items-center gap-3.5 text-[var(--text-color)] hover:text-[var(--sahi-green)] transition-colors no-underline group/email"
                          >
                            <div className="w-9 h-9 md:w-11 md:h-11 rounded-lg bg-[var(--sahi-green-light)] text-[var(--sahi-green)] flex items-center justify-center shrink-0 group-hover/email:bg-[var(--sahi-green)] group-hover/email:text-white transition-colors">
                              <Mail className="h-5 w-5" />
                            </div>
                            <span className="font-medium truncate text-sm">{email}</span>
                          </a>
                        )}
                        {mobile && (
                          <a
                            href={`tel:${mobile}`}
                            onClick={handlePhoneClick}
                            className="flex items-center gap-3.5 text-[var(--text-color)] hover:text-[var(--sahi-green)] transition-colors no-underline group/phone cursor-pointer"
                            title={!isMobile ? "Copy phone number" : "Call Business"}
                          >
                            <div className="w-9 h-9 md:w-11 md:h-11 rounded-lg bg-[var(--sahi-green-light)] text-[var(--sahi-green)] flex items-center justify-center shrink-0 group-hover/phone:bg-[var(--sahi-green)] group-hover/phone:text-white transition-colors">
                              <Phone className="h-5 w-5" />
                            </div>
                            <span className="font-medium text-sm">{mobile}</span>
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Card 4: About Us Description */}
                  {description && (
                    <div className="bg-white border border-[var(--border)] hover:border-[var(--sahi-green)]/30 rounded-xl p-4 sm:p-5 md:p-6 shadow-xs hover:shadow-sm transition-all">
                      <h2 className="text-base sm:text-lg md:text-base font-bold text-[var(--text-color)] mb-2.5">
                        About Us
                      </h2>
                      <p className="text-sm text-[var(--text-color)] leading-relaxed font-normal whitespace-pre-wrap">
                        {description}
                      </p>
                    </div>
                  )}

                  {/* Powered by SahiClub Badge (Desktop Right Sidebar) */}
                  {!showBackButton && (
                    <div className="hidden lg:flex items-center justify-center gap-1.5 pt-3 pb-1 text-center">
                      <span className="text-xs text-[var(--sub-text-color)] font-medium">
                        Powered by
                      </span>
                      <a
                        href="https://sahi.club"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 hover:opacity-85 transition-opacity"
                        title="SahiClub"
                      >
                        <img
                          src={getImageRoot('Images', 'SahiLogo.png')}
                          alt="SahiClub"
                          style={{ height: "22px", width: "auto" }}
                          className="object-contain"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Powered by SahiClub Footer Panel (Mobile / Left Col) */}
            {!showBackButton && (
              <div className={`${hasSidebar ? "lg:hidden" : ""} pt-6 pb-2 flex flex-col items-center justify-center text-center space-y-1.5 mt-6`}>
                <span className="text-xs text-[var(--sub-text-color)] font-medium tracking-wide">
                  Powered by
                </span>
                <a
                  href="https://sahi.club/App"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 hover:opacity-85 transition-opacity"
                  title="SahiClub App"
                >
                  <img
                    src={getImageRoot('Images', 'SahiLogo.png')}
                    alt="SahiClub"
                    style={{ height: "24px", width: "auto" }}
                    className="object-contain"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile-Only Sticky Floating Action Bar (Hidden) */}
      <div className="hidden lg:hidden fixed bottom-0 left-0 right-0 p-3 bg-white/95 backdrop-blur-sm z-30 shadow-lg flex items-center justify-center gap-2 max-w-lg mx-auto">
        <Button
          onClick={handleScrollToForm}
          variant="sahiGreen"
          className="flex-1 h-10 flex items-center justify-center font-semibold text-sm cursor-pointer"
        >
          Inquire Now
        </Button>
        {mobile && (
          <a
            href={`tel:${mobile}`}
            className="w-10 h-10 rounded-md bg-[var(--sahi-green-light)] border border-[var(--border)] flex items-center justify-center text-[var(--sahi-green)] shrink-0 active:scale-95 transition-transform"
            title="Call Business"
          >
            <Phone className="h-4 w-4" />
          </a>
        )}
        {whatsappUrl && (
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-md bg-[var(--sahi-green-light)] border border-[var(--border)] flex items-center justify-center text-[var(--sahi-green)] shrink-0 active:scale-95 transition-transform"
            title="WhatsApp"
          >
            <BsWhatsapp className="h-4 w-4" />
          </a>
        )}
      </div>

      {/* Zoom Image Lightbox Overlay (Portaled to document.body to bypass Radix Dialog pointer-events lock) */}
      {zoomImageUrl &&
        createPortal(
          <div
            className="fixed inset-0 z-[100005] bg-black/85 backdrop-blur-xs flex items-center justify-center p-4 select-none animate-in fade-in duration-200 pointer-events-auto"
            data-radix-portal=""
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleCloseZoom}
          >
            <button
              type="button"
              className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/90 hover:text-white bg-black/60 hover:bg-black/80 p-2.5 rounded-full transition-all cursor-pointer z-[100006] shadow-lg border border-white/20 flex items-center justify-center pointer-events-auto"
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerUp={(e) => {
                e.stopPropagation();
                handleCloseZoom();
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleCloseZoom();
              }}
              title="Close fullscreen"
            >
              <X className="h-6 w-6" />
              <span className="sr-only">Close fullscreen</span>
            </button>
            <div
              className="relative flex items-center justify-center cursor-default pointer-events-auto overflow-hidden rounded-2xl border border-white/20 shadow-2xl bg-white"
              style={{
                width: "85vmin",
                height: "85vmin",
                maxWidth: "650px",
                maxHeight: "650px",
                aspectRatio: "1 / 1"
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={zoomImageUrl}
                alt="Offering Zoom Preview"
                className="w-full h-full object-cover"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          </div>,
          document.body
        )}

      {/* Offering Inquiry Modal / Mobile Drawer */}
      {selectedOffering && (() => {
        const detailImgUrl = getOfferingImageUrl(
          selectedOffering.PImg ||
          selectedOffering.Img ||
          selectedOffering.img ||
          selectedOffering.imageUrl ||
          selectedOffering.ImgURL ||
          selectedOffering.image
        );

        const productName = selectedOffering.PNm || selectedOffering.name || "Offering";
        const offeringDescription =
          selectedOffering.PTxt ||
          selectedOffering.pTxt ||
          selectedOffering.description ||
          selectedOffering.Desc ||
          selectedOffering.Description ||
          selectedOffering.PDesc ||
          "";
        const rate = parseFloat(selectedOffering.Rate || selectedOffering.price) || 0;

        if (isMobile) {
          return (
            <Drawer
              open={Boolean(selectedOffering) && !zoomImageUrl}
              onOpenChange={(isOpen) => {
                if (!isOpen) {
                  resetOfferingModalState();
                }
              }}
              shouldScaleBackground={false}
              repositionInputs={false}
            >
              <DrawerContent
                onFocusCapture={handleDrawerFieldFocus}
                className="p-0 [&>div:first-child]:hidden !top-auto !bottom-0 !h-auto max-h-[90dvh] rounded-t-[22px] border-t border-[var(--border)] z-[10002] overflow-hidden bg-background flex flex-col"
                style={{
                  '--sahi-primary-color': '#0b5d1e'
                }}
              >
                {/* Header */}
                <div className="flex items-center px-4 h-14 shrink-0 bg-background rounded-t-[22px]">
                  <div className="flex gap-3 items-center min-w-0 flex-1">
                    <button
                      type="button"
                      tabIndex={-1}
                      className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500 hover:text-gray-800 cursor-pointer"
                      onClick={() => {
                        if (mobileInquiryStep === 2) {
                          setMobileInquiryStep(1);
                        } else {
                          resetOfferingModalState();
                        }
                      }}
                      title="Back"
                    >
                      <ArrowLeft className="h-6 w-6" />
                      <span className="sr-only">Back</span>
                    </button>
                    <p className="text-lg font-semibold text-gray-900 truncate">
                      {mobileInquiryStep === 2 ? "Contact Details" : "Inquiry"}
                    </p>
                  </div>
                </div>

                {/* Step 1: Needs / Offering View */}
                {mobileInquiryStep === 1 && (
                  <form
                    noValidate
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!offeringInquiryForm.needs.trim()) {
                        toast.error("Please enter your requirement.");
                        const needsInput = document.getElementById("mobile-drawer-webstore-needs-input");
                        if (needsInput) {
                          needsInput.focus();
                        }
                        return;
                      }
                      setMobileInquiryStep(2);
                    }}
                    className="flex-1 min-h-0 flex flex-col overflow-hidden"
                  >
                    <div className="overflow-y-auto custom-scroll flex-1 min-h-0 p-4 pb-10 space-y-4 drawer-form-scroll scroll-pt-3 scroll-pb-10">
                      {/* Selected Offering Summary: Full Width Image, Left-aligned Name & Description */}
                      <div className="flex flex-col items-start text-left space-y-2 w-full">
                        {/* Image */}
                        <div className="w-full relative rounded-xl sm:rounded-2xl overflow-hidden border border-[var(--border)] bg-zinc-50 h-48 sm:h-56 flex items-center justify-center">
                          {detailImgUrl ? (
                            <img
                              src={detailImgUrl}
                              alt={productName}
                              className="w-full h-full object-contain cursor-zoom-in group/zoom bg-zinc-50"
                              onClick={() => {
                                if (detailImgUrl) setZoomImageUrl(detailImgUrl);
                              }}
                              title="Click to view full screen"
                              onError={(e) => {
                                e.target.style.display = "none";
                                if (e.target.nextElementSibling) {
                                  e.target.nextElementSibling.style.display = "flex";
                                }
                              }}
                            />
                          ) : null}
                          <div className={`${detailImgUrl ? "hidden" : "flex"} w-full h-full bg-zinc-100 items-center justify-center text-zinc-400`}>
                            <ImageIcon className="w-12 h-12 opacity-40" />
                          </div>
                        </div>

                        {/* Product Name & Description */}
                        <div className="w-full text-left space-y-0.5 pt-1">
                          <div className="flex items-start justify-between gap-3">
                            <h3
                              className="text-lg sm:text-xl font-bold text-[var(--text-color)] text-left break-words leading-snug"
                              title={productName}
                            >
                              {productName}
                            </h3>
                            {rate > 0 && (
                              <div className="shrink-0 pt-0.5 text-left">
                                <span className="inline-flex items-center text-xs font-bold text-[var(--sahi-green)] bg-[var(--sahi-green-light)] px-2.5 py-1 rounded-md border border-[var(--sahi-green)]/15">
                                  ₹{rate.toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>

                          {offeringDescription ? (
                            <div className="text-sm text-[var(--sub-text-color)] leading-relaxed pt-0.5 max-h-24 overflow-y-auto custom-scroll break-words whitespace-pre-line text-left w-full">
                              {offeringDescription}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Divider */}
                      <hr className="border-t border-[var(--border)] !mt-3" />

                      {/* Requirement (Compulsory) */}
                      <div className="space-y-1.5 text-left !mt-3 drawer-field scroll-mt-3">
                        <Label className="text-sm font-medium text-[var(--text-color)]">
                          Requirement <span className="text-red-500 font-bold">*</span>
                        </Label>
                        <div className="relative">
                          <Textarea
                            id="mobile-drawer-webstore-needs-input"
                            rows={3}
                            required
                            placeholder="e.g. quantity, specifications..."
                            value={offeringInquiryForm.needs}
                            onChange={(e) =>
                              setOfferingInquiryForm((prev) => ({ ...prev, needs: e.target.value }))
                            }
                            className="text-sm sm:text-base text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg min-h-[84px] resize-y pr-14"
                          />
                          <SpeechControls
                            targetId="mobile-drawer-webstore-needs-input"
                            value={offeringInquiryForm.needs}
                            onChange={(val) =>
                              setOfferingInquiryForm((prev) => ({ ...prev, needs: val }))
                            }
                            isMobile={isMobile}
                            className="absolute right-2 top-2"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="px-4 py-2.5 sm:py-3 flex items-center bg-background shrink-0 border-t border-[var(--border)]/40">
                      <Button
                        type="submit"
                        variant="sahiGreen"
                        className="w-full h-11 font-semibold text-sm flex items-center justify-center gap-1.5 cursor-pointer rounded-lg shadow-xs"
                      >
                        <Send className="h-4 w-4" /> Submit Inquiry
                      </Button>
                    </div>
                  </form>
                )}

                {/* Step 2: Personal Details Drawer */}
                {mobileInquiryStep === 2 && (
                  <form
                    noValidate
                    onSubmit={handleOfferingInquirySubmit}
                    className="flex-1 min-h-0 flex flex-col overflow-hidden"
                  >
                    <div className="overflow-y-auto custom-scroll flex-1 min-h-0 p-4 pb-10 space-y-4 drawer-form-scroll scroll-pt-3 scroll-pb-10">
                      {/* Mobile Number */}
                      <div className="space-y-1.5 text-left drawer-field scroll-mt-3">
                        <Label className="text-sm font-medium text-[var(--text-color)]">
                          Mobile Number <span className="text-red-500 font-bold">*</span>
                        </Label>
                        <MobileInput
                          id="mobile-drawer-contact-phone"
                          value={offeringInquiryForm.mobile}
                          onChange={(e) => {
                            const val = e.target.value;
                            setOfferingInquiryForm((prev) => ({ ...prev, mobile: val }));
                          }}
                          selectedCountryCode={offeringInquiryForm.cc || "91"}
                          onCountryCodeChange={(newCC) => {
                            setOfferingInquiryForm((prev) => ({ ...prev, cc: newCC }));
                          }}
                          placeholder="e.g. 9876543210"
                          className="w-full"
                        />
                      </div>

                      {/* Your Name */}
                      <div className="space-y-1.5 text-left drawer-field scroll-mt-3">
                        <Label className="text-sm font-medium text-[var(--text-color)]">
                          Your Name <span className="text-red-500 font-bold">*</span>
                        </Label>
                        <Input
                          type="text"
                          required
                          placeholder="e.g. John Doe"
                          value={offeringInquiryForm.name}
                          onChange={(e) =>
                            setOfferingInquiryForm((prev) => ({ ...prev, name: e.target.value }))
                          }
                          className="h-11 text-sm sm:text-base text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg"
                        />
                      </div>

                      {/* Company Name */}
                      <div className="space-y-1.5 text-left drawer-field scroll-mt-3">
                        <Label className="text-sm font-medium text-[var(--text-color)]">
                          Company Name
                        </Label>
                        <Input
                          type="text"
                          placeholder="e.g. Acme Corp"
                          value={offeringInquiryForm.company}
                          onChange={(e) =>
                            setOfferingInquiryForm((prev) => ({ ...prev, company: e.target.value }))
                          }
                          className="h-11 text-sm sm:text-base text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg"
                        />
                      </div>

                      {/* Email Address */}
                      <div className="space-y-1.5 text-left drawer-field scroll-mt-3">
                        <Label className="text-sm font-medium text-[var(--text-color)]">
                          Email Address
                        </Label>
                        <Input
                          type="email"
                          placeholder="e.g. john@example.com"
                          value={offeringInquiryForm.email}
                          onChange={(e) =>
                            setOfferingInquiryForm((prev) => ({ ...prev, email: e.target.value }))
                          }
                          className="h-11 text-sm sm:text-base text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg"
                        />
                      </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="px-4 py-2.5 sm:py-3 flex items-center bg-background shrink-0 border-t border-[var(--border)]/40">
                      <Button
                        type="submit"
                        variant="sahiGreen"
                        disabled={submittingOfferingInquiry}
                        className="w-full h-11 font-semibold text-sm flex items-center justify-center gap-1.5 cursor-pointer rounded-lg shadow-xs"
                      >
                        {submittingOfferingInquiry ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" /> Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4" /> Submit Inquiry
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                )}
              </DrawerContent>
            </Drawer>
          );
        }

        return (
          <Dialog
            open={Boolean(selectedOffering)}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                resetOfferingModalState();
              }
            }}
          >
            <DialogContent
              onEscapeKeyDown={(e) => {
                if (zoomImageUrl) {
                  e.preventDefault();
                  handleCloseZoom();
                }
              }}
              className="w-full max-w-2xl max-h-[90vh] flex flex-col p-0 overflow-hidden rounded-xl border border-[var(--border)] bg-background shadow-2xl outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0"
              style={{ '--sahi-primary-color': '#0b5d1e', outline: 'none' }}
            >
              {/* Inquiry View Header */}
              <DialogHeader className="gap-0 p-4 shrink-0 flex flex-row items-center justify-between bg-background">
                <DialogTitle className="text-xl font-bold text-[var(--text-color)] truncate pr-2 text-left">
                  Inquiry
                </DialogTitle>
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={resetOfferingModalState}
                  className="rounded-lg p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors cursor-pointer shrink-0"
                  title="Close"
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </button>
              </DialogHeader>

              {/* Inquiry Form */}
              <form
                noValidate
                onSubmit={handleOfferingInquirySubmit}
                className="flex-1 flex flex-col overflow-hidden"
              >
                <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[calc(90vh-130px)] custom-scroll">
                  {/* Selected Offering Summary */}
                  <div className="flex items-start gap-4 max-w-full">
                    {detailImgUrl ? (
                      <img
                        src={detailImgUrl}
                        alt={productName}
                        className="w-24 h-24 rounded-xl object-cover shrink-0 cursor-zoom-in group/zoom border border-[var(--border)]"
                        onClick={() => {
                          if (detailImgUrl) setZoomImageUrl(detailImgUrl);
                        }}
                        title="Click to view full screen"
                        onError={(e) => {
                          e.target.style.display = "none";
                          if (e.target.nextElementSibling) {
                            e.target.nextElementSibling.style.display = "flex";
                          }
                        }}
                      />
                    ) : null}
                    <div className={`${detailImgUrl ? "hidden" : "flex"} w-24 h-24 rounded-xl bg-zinc-100 items-center justify-center text-zinc-400 shrink-0 border border-[var(--border)]`}>
                      <ImageIcon className="w-8 h-8 opacity-40" />
                    </div>
                    <div className="flex-1 min-w-0 text-left pr-2 flex flex-col justify-start">
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-2xl font-bold text-[var(--text-color)] break-words leading-tight" title={productName}>
                          {productName}
                        </div>
                        {rate > 0 && (
                          <div className="shrink-0 pt-0.5">
                            <span className="inline-flex items-center text-xs font-bold text-[var(--sahi-green)] bg-[var(--sahi-green-light)] px-2.5 py-1 rounded-md border border-[var(--sahi-green)]/15">
                              ₹{rate.toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                      {offeringDescription ? (
                        <div className="text-base text-[var(--sub-text-color)] leading-relaxed mt-0.5 max-h-24 sm:max-h-28 overflow-y-auto custom-scroll break-words whitespace-pre-line pr-1.5">
                          {offeringDescription}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Requirement (Compulsory) */}
                  <div className="space-y-1.5 text-left">
                    <Label className="text-sm font-medium text-[var(--text-color)]">
                      Requirement <span className="text-red-500 font-bold">*</span>
                    </Label>
                    <div className="relative">
                      <Textarea
                        id="desktop-modal-webstore-needs-input"
                        rows={3}
                        required
                        placeholder="e.g. quantity, specifications..."
                        value={offeringInquiryForm.needs}
                        onChange={(e) =>
                          setOfferingInquiryForm((prev) => ({ ...prev, needs: e.target.value }))
                        }
                        className="text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg min-h-[90px] resize-y pr-14"
                      />
                      <SpeechControls
                        targetId="desktop-modal-webstore-needs-input"
                        value={offeringInquiryForm.needs}
                        onChange={(val) =>
                          setOfferingInquiryForm((prev) => ({ ...prev, needs: val }))
                        }
                        isMobile={isMobile}
                        className="absolute right-2 top-2"
                      />
                    </div>
                  </div>

                  {/* Contact Details in 2 columns */}
                  <div className="space-y-4 text-left">
                    {/* Row 1: Name and Mobile */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Your Name */}
                      <div className="space-y-1.5 text-left">
                        <Label className="text-sm font-medium text-[var(--text-color)]">
                          Your Name <span className="text-red-500 font-bold">*</span>
                        </Label>
                        <Input
                          type="text"
                          required
                          placeholder="e.g. John Doe"
                          value={offeringInquiryForm.name}
                          onChange={(e) =>
                            setOfferingInquiryForm((prev) => ({ ...prev, name: e.target.value }))
                          }
                          className="h-10 text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg"
                        />
                      </div>

                      {/* Mobile Number */}
                      <div className="space-y-1.5 text-left">
                        <Label className="text-sm font-medium text-[var(--text-color)]">
                          Mobile Number <span className="text-red-500 font-bold">*</span>
                        </Label>
                        <MobileInput
                          value={offeringInquiryForm.mobile}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, "");
                            setOfferingInquiryForm((prev) => ({ ...prev, mobile: val }));
                          }}
                          selectedCountryCode={offeringInquiryForm.cc || "91"}
                          onCountryCodeChange={(newCC) => {
                            setOfferingInquiryForm((prev) => ({ ...prev, cc: newCC }));
                          }}
                          placeholder="e.g. 9876543210"
                          className="w-full"
                          containerClassName="h-10 rounded-lg"
                        />
                      </div>
                    </div>

                    {/* Row 2: Company and Email */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Company Name */}
                      <div className="space-y-1.5 text-left">
                        <Label className="text-sm font-medium text-[var(--text-color)]">
                          Company Name
                        </Label>
                        <Input
                          type="text"
                          placeholder="e.g. Acme Corp"
                          value={offeringInquiryForm.company}
                          onChange={(e) =>
                            setOfferingInquiryForm((prev) => ({ ...prev, company: e.target.value }))
                          }
                          className="h-10 text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg"
                        />
                      </div>

                      {/* Email Address */}
                      <div className="space-y-1.5 text-left">
                        <Label className="text-sm font-medium text-[var(--text-color)]">
                          Email Address
                        </Label>
                        <Input
                          type="email"
                          placeholder="e.g. john@example.com"
                          value={offeringInquiryForm.email}
                          onChange={(e) =>
                            setOfferingInquiryForm((prev) => ({ ...prev, email: e.target.value }))
                          }
                          className="h-10 text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Inquiry and Cancel Buttons */}
                <div className="p-4 flex flex-row items-center justify-start gap-4 shrink-0 bg-background">
                  <Button
                    type="submit"
                    variant="sahiGreen"
                    disabled={submittingOfferingInquiry}
                    className="px-5 h-10 font-semibold text-sm flex items-center justify-center gap-1.5 cursor-pointer rounded-lg shadow-xs"
                  >
                    {submittingOfferingInquiry ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" /> Submit Inquiry
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={submittingOfferingInquiry}
                    onClick={resetOfferingModalState}
                    className="sahi-secondary-button px-5 h-10 font-semibold text-sm cursor-pointer rounded-lg flex items-center justify-center gap-1.5"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* Mobile Direct Inquiry Contact Details Drawer */}
      {isMobile && (
        <Drawer
          open={isDirectInquiryContactDrawerOpen}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setIsDirectInquiryContactDrawerOpen(false);
            }
          }}
          shouldScaleBackground={false}
          repositionInputs={false}
        >
          <DrawerContent
            onFocusCapture={handleDrawerFieldFocus}
            className="p-0 [&>div:first-child]:hidden !top-auto !bottom-0 !h-auto max-h-[90dvh] rounded-t-[22px] border-t border-[var(--border)] z-[10002] overflow-hidden bg-background flex flex-col"
            style={{
              '--sahi-primary-color': '#0b5d1e'
            }}
          >
            {/* Header */}
            <div className="flex items-center px-4 h-14 shrink-0 bg-background rounded-t-[22px]">
              <div className="flex gap-3 items-center min-w-0 flex-1">
                <button
                  type="button"
                  tabIndex={-1}
                  className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500 hover:text-gray-800 cursor-pointer"
                  onClick={() => {
                    setIsDirectInquiryContactDrawerOpen(false);
                  }}
                  title="Back"
                >
                  <ArrowLeft className="h-6 w-6" />
                  <span className="sr-only">Back</span>
                </button>
                <p className="text-lg font-semibold text-gray-900 truncate">
                  Contact Details
                </p>
              </div>
            </div>

            {/* Direct Inquiry Contact Form */}
            <form
              noValidate
              onSubmit={handleInquirySubmit}
              className="flex-1 min-h-0 flex flex-col overflow-hidden"
            >
              <div className="overflow-y-auto custom-scroll flex-1 min-h-0 p-4 pb-10 space-y-4 drawer-form-scroll scroll-pt-3 scroll-pb-10">
                {/* Mobile Number */}
                <div className="space-y-1.5 text-left drawer-field scroll-mt-3">
                  <Label className="text-sm font-medium text-[var(--text-color)]">
                    Mobile Number <span className="text-red-500 font-bold">*</span>
                  </Label>
                  <MobileInput
                    value={inquiryForm.mobile}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, "");
                      setInquiryForm((prev) => ({ ...prev, mobile: val }));
                    }}
                    selectedCountryCode={inquiryForm.cc || "91"}
                    onCountryCodeChange={(newCC) => {
                      setInquiryForm((prev) => ({ ...prev, cc: newCC }));
                    }}
                    placeholder="e.g. 9876543210"
                    className="w-full"
                  />
                </div>

                {/* Your Name */}
                <div className="space-y-1.5 text-left drawer-field scroll-mt-3">
                  <Label className="text-sm font-medium text-[var(--text-color)]">
                    Your Name <span className="text-red-500 font-bold">*</span>
                  </Label>
                  <Input
                    type="text"
                    required
                    placeholder="e.g. John Doe"
                    value={inquiryForm.name}
                    onChange={(e) =>
                      setInquiryForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="h-11 text-sm sm:text-base text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg"
                  />
                </div>

                {/* Company Name */}
                <div className="space-y-1.5 text-left drawer-field scroll-mt-3">
                  <Label className="text-sm font-medium text-[var(--text-color)]">
                    Company Name
                  </Label>
                  <Input
                    type="text"
                    placeholder="e.g. Acme Corp"
                    value={inquiryForm.company}
                    onChange={(e) =>
                      setInquiryForm((prev) => ({ ...prev, company: e.target.value }))
                    }
                    className="h-11 text-sm sm:text-base text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg"
                  />
                </div>

                {/* Email Address */}
                <div className="space-y-1.5 text-left drawer-field scroll-mt-3">
                  <Label className="text-sm font-medium text-[var(--text-color)]">
                    Email Address
                  </Label>
                  <Input
                    type="email"
                    placeholder="e.g. john@example.com"
                    value={inquiryForm.email}
                    onChange={(e) =>
                      setInquiryForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    className="h-11 text-sm sm:text-base text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg"
                  />
                </div>
              </div>

              {/* Footer Actions */}
              <div className="px-4 py-2.5 sm:py-3 flex items-center bg-background shrink-0 border-t border-[var(--border)]/40">
                <Button
                  type="submit"
                  variant="sahiGreen"
                  disabled={submittingInquiry}
                  className="w-full h-11 font-semibold text-sm flex items-center justify-center gap-1.5 cursor-pointer rounded-lg shadow-xs"
                >
                  {submittingInquiry ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" /> Submit Inquiry
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DrawerContent>
        </Drawer>
      )}

      {/* Welcome Contact Details Modal (Desktop) - Unclosable until submitted */}
      {!isMobile && (
        <Dialog
          open={isContactPromptOpen}
          forceOpen={true}
        >
          <DialogContent
            showCloseButton={false}
            onEscapeKeyDown={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
            overlayClassName="backdrop-blur-sm bg-black/60"
            className="w-full max-w-2xl max-h-[90vh] flex flex-col p-0 overflow-hidden rounded-xl border border-[var(--border)] bg-background shadow-2xl outline-none focus:outline-none ring-0"
            style={{ '--sahi-primary-color': '#0b5d1e' }}
          >
            <DialogHeader className="p-5 pb-4 shrink-0 flex flex-col items-start bg-background text-left">
              <DialogTitle className="text-xl font-bold text-[var(--text-color)] truncate max-w-full">
                {welcomeTitle}
              </DialogTitle>
            </DialogHeader>

            <form
              noValidate
              onSubmit={handleContactSubmit}
              className="flex-1 flex flex-col overflow-hidden"
            >
              <div className="px-4 space-y-4 overflow-y-auto custom-scroll text-left">
                {/* Row 1: Your Name and Mobile Number */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Your Name */}
                  <div className="space-y-1.5 text-left">
                    <Label className="text-sm font-medium text-[var(--text-color)]">
                      Your Name <span className="text-red-500 font-bold">*</span>
                    </Label>
                    <Input
                      type="text"
                      required
                      placeholder="e.g. John Doe"
                      value={contactForm.name}
                      onChange={(e) =>
                        setContactForm((prev) => ({ ...prev, name: e.target.value }))
                      }
                      className="h-11 text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg"
                    />
                  </div>

                  {/* Mobile Number */}
                  <div className="space-y-1.5 text-left">
                    <Label className="text-sm font-medium text-[var(--text-color)]">
                      Mobile Number <span className="text-red-500 font-bold">*</span>
                    </Label>
                    <MobileInput
                      value={contactForm.mobile}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, "");
                        setContactForm((prev) => ({ ...prev, mobile: val }));
                      }}
                      selectedCountryCode={contactForm.cc || "91"}
                      onCountryCodeChange={(newCC) => {
                        setContactForm((prev) => ({ ...prev, cc: newCC }));
                      }}
                      placeholder="e.g. 9876543210"
                      className="w-full"
                      containerClassName="h-10 rounded-lg"
                    />
                  </div>
                </div>

                {/* Row 2: Company Name and Email Address */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Company Name */}
                  <div className="space-y-1.5 text-left">
                    <Label className="text-sm font-medium text-[var(--text-color)]">
                      Company Name
                    </Label>
                    <Input
                      type="text"
                      placeholder="e.g. Acme Corp"
                      value={contactForm.company}
                      onChange={(e) =>
                        setContactForm((prev) => ({ ...prev, company: e.target.value }))
                      }
                      className="h-11 text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg"
                    />
                  </div>

                  {/* Email Address */}
                  <div className="space-y-1.5 text-left">
                    <Label className="text-sm font-medium text-[var(--text-color)]">
                      Email Address
                    </Label>
                    <Input
                      type="email"
                      placeholder="e.g. john@example.com"
                      value={contactForm.email}
                      onChange={(e) =>
                        setContactForm((prev) => ({ ...prev, email: e.target.value }))
                      }
                      className="h-11 text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg"
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 shrink-0 bg-background flex items-center justify-start">
                <Button
                  type="submit"
                  variant="sahiGreen"
                  disabled={submittingContactDetails}
                  className="px-6 h-10 font-semibold text-sm flex items-center justify-center gap-1.5 cursor-pointer rounded-lg shadow-xs"
                >
                  {submittingContactDetails ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Entering...
                    </>
                  ) : (
                    <>
                      Enter Webstore <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Welcome Contact Details Drawer (Mobile) - Unclosable until submitted */}
      {isMobile && (
        <Drawer
          open={isContactPromptOpen}
          dismissible={false}
          shouldScaleBackground={false}
          repositionInputs={false}
        >
          <DrawerContent
            overlayClassName="backdrop-blur-sm bg-black/60"
            onFocusCapture={handleDrawerFieldFocus}
            className="p-0 [&>div:first-child]:hidden !top-auto !bottom-0 !h-auto max-h-[90dvh] rounded-t-[22px] border-t border-[var(--border)] z-[10002] overflow-hidden bg-background flex flex-col"
            style={{
              '--sahi-primary-color': '#0b5d1e'
            }}
          >
            {/* Header */}
            <div className="flex flex-col justify-center px-5 py-3 shrink-0 bg-background rounded-t-[22px] text-left h-16">
              <p className="text-lg font-semibold text-gray-900 truncate">
                {welcomeTitle}
              </p>
            </div>

            {/* Form */}
            <form
              noValidate
              onSubmit={handleContactSubmit}
              className="flex-1 min-h-0 flex flex-col overflow-hidden"
            >
              <div className="overflow-y-auto custom-scroll flex-1 min-h-0 p-4 pt-0 pb-10 space-y-4 drawer-form-scroll scroll-pt-3 scroll-pb-10">
                {/* Your Name */}
                <div className="space-y-1.5 text-left drawer-field scroll-mt-3">
                  <Label className="text-sm font-medium text-[var(--text-color)]">
                    Your Name <span className="text-red-500 font-bold">*</span>
                  </Label>
                  <Input
                    type="text"
                    required
                    placeholder="e.g. John Doe"
                    value={contactForm.name}
                    onChange={(e) =>
                      setContactForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="h-11 text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg"
                  />
                </div>

                {/* Mobile Number */}
                <div className="space-y-1.5 text-left drawer-field scroll-mt-3">
                  <Label className="text-sm font-medium text-[var(--text-color)]">
                    Mobile Number <span className="text-red-500 font-bold">*</span>
                  </Label>
                  <MobileInput
                    value={contactForm.mobile}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, "");
                      setContactForm((prev) => ({ ...prev, mobile: val }));
                    }}
                    selectedCountryCode={contactForm.cc || "91"}
                    onCountryCodeChange={(newCC) => {
                      setContactForm((prev) => ({ ...prev, cc: newCC }));
                    }}
                    placeholder="e.g. 9876543210"
                    className="w-full"
                  />
                </div>

                {/* Company Name */}
                <div className="space-y-1.5 text-left drawer-field scroll-mt-3">
                  <Label className="text-sm font-medium text-[var(--text-color)]">
                    Company Name
                  </Label>
                  <Input
                    type="text"
                    placeholder="e.g. Acme Corp"
                    value={contactForm.company}
                    onChange={(e) =>
                      setContactForm((prev) => ({ ...prev, company: e.target.value }))
                    }
                    className="h-11 text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg"
                  />
                </div>

                {/* Email Address */}
                <div className="space-y-1.5 text-left drawer-field scroll-mt-3">
                  <Label className="text-sm font-medium text-[var(--text-color)]">
                    Email Address
                  </Label>
                  <Input
                    type="email"
                    placeholder="e.g. john@example.com"
                    value={contactForm.email}
                    onChange={(e) =>
                      setContactForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    className="h-11 text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg"
                  />
                </div>
              </div>

              {/* Footer Action */}
              <div className="px-4 py-2.5 sm:py-3 flex items-center bg-background shrink-0 border-t border-[var(--border)]/40">
                <Button
                  type="submit"
                  variant="sahiGreen"
                  disabled={submittingContactDetails}
                  className="w-full h-11 font-semibold text-sm flex items-center justify-center gap-1.5 cursor-pointer rounded-lg shadow-xs"
                >
                  {submittingContactDetails ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Entering...
                    </>
                  ) : (
                    <>
                      Enter Webstore <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DrawerContent>
        </Drawer>
      )}

      {/* SahiCRM Mobile Menu Drawer */}
      <MenuDrawer
        isOpen={isMenuDrawerOpen}
        onClose={() => setIsMenuDrawerOpen(false)}
      />
    </div>
  );
}

